import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { PALETTE as P } from '../tokens';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api/auth';
import { dailyCheckinApi } from '../api/dailyCheckin';
import { ouraApi } from '../api/oura';
import { isOuraUIEnabled } from '../utils/features';
import { usersApi } from '../api/users';
import { AIDisclosureBody, AIDeclineExplainer } from '../components/AIConsentModal';
import {
  MONTHS,
  SEX_OPTIONS,
  MIN_AGE,
  daysIn,
  dayOptions,
  yearOptions,
  assembleDob,
  ageFromDob,
} from '../utils/dob';
import {
  saveOnboardingDraft,
  loadOnboardingDraft,
  clearOnboardingDraft,
} from '../utils/onboardingDraft';
import { connectHealthKit, isHealthKitSupported } from '../services/healthkit';
import { Wordmark } from '../components/Wordmark';
import { isIOSBrowser, isNativeIOS } from '../utils/platform';

/* ------------------------------------------------------------------ */
/* Life-stage options (Screen 3)                                       */
/* ------------------------------------------------------------------ */

const LIFE_STAGES = [
  { id: 'building', label: 'Building something — work is full on' },
  { id: 'growing', label: 'Career is demanding — a lot is riding on this chapter' },
  { id: 'holding', label: 'Holding a lot — work, family, everything at once' },
  { id: 'crossroads', label: 'At a crossroads — figuring out what\u2019s next' },
];

/* ------------------------------------------------------------------ */
/* Scoped styles                                                       */
/* ------------------------------------------------------------------ */

const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .ob-root {
    font-family: 'DM Sans', sans-serif;
    background: ${P.bg};
    min-height: 100vh;
    min-height: 100dvh;
    color: ${P.text};
    max-width: 420px;
    margin: 0 auto;
    position: relative;
  }
  .serif { font-family: 'Playfair Display', Georgia, serif; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fu  { animation: fadeUp 0.48s ease forwards; }
  .d1  { animation-delay: 0.08s; opacity: 0; }
  .d2  { animation-delay: 0.20s; opacity: 0; }
  .d3  { animation-delay: 0.34s; opacity: 0; }
  .d4  { animation-delay: 0.48s; opacity: 0; }
  .d5  { animation-delay: 0.62s; opacity: 0; }

  .ob-btn {
    width: 100%;
    padding: 17px;
    background: ${P.dark};
    border: none;
    border-radius: 16px;
    cursor: pointer;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 18px;
    font-style: italic;
    color: ${P.bg};
    transition: opacity 0.15s;
    letter-spacing: 0.01em;
  }
  .ob-btn:hover { opacity: 0.85; }
  .ob-btn:disabled { opacity: 0.35; cursor: default; }

  .ob-btn-ghost {
    width: 100%;
    padding: 15px;
    background: none;
    border: 1.5px solid ${P.border};
    border-radius: 16px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: ${P.textMid};
    transition: all 0.15s;
  }
  .ob-btn-ghost:hover { border-color: ${P.primaryMid}; color: ${P.primary}; }

  .ob-textarea {
    width: 100%;
    min-height: 160px;
    background: ${P.surface};
    border: 1.5px solid ${P.border};
    border-radius: 16px;
    padding: 18px;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 300;
    color: ${P.text};
    line-height: 1.65;
    outline: none;
    resize: vertical;
    transition: border-color 0.2s;
  }
  .ob-textarea:focus { border-color: ${P.primaryMid}; }
  .ob-textarea::placeholder { color: ${P.textFaint}; }

  .ob-input {
    width: 100%;
    background: ${P.surface};
    border: 1.5px solid ${P.border};
    border-radius: 16px;
    padding: 16px 18px;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 300;
    color: ${P.text};
    line-height: 1.4;
    outline: none;
    transition: border-color 0.2s;
  }
  .ob-input:focus { border-color: ${P.primaryMid}; }

  .ob-stage-card {
    background: ${P.surface};
    border: 1.5px solid ${P.border};
    border-radius: 16px;
    padding: 16px 18px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .ob-stage-card.selected {
    border-color: ${P.primary};
    background: ${P.primarySoft};
  }
  .ob-stage-card:hover:not(.selected) { border-color: ${P.primaryMid}; }
`;

/* ------------------------------------------------------------------ */
/* Progress dots                                                       */
/* ------------------------------------------------------------------ */

function ProgressDots({ current, total }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: i <= current ? P.primary : P.border,
            transition: 'background 0.3s ease',
          }}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Back button                                                         */
/* ------------------------------------------------------------------ */

function BackButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: 12,
        color: P.textMute,
        padding: '4px 0',
        transition: 'color 0.15s',
      }}
    >
      &larr; Back
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

export default function Onboarding() {
  const navigate = useNavigate();
  // Set when AuthPage just redeemed an invite/event code (?code=GOLF) —
  // the confirmation the signup never used to get.
  const routerLocation = useLocation();
  const codeApplied = !!routerLocation.state?.codeApplied;
  const { user, token, patchUser } = useAuth();

  // The survival draft exists *only* to carry answers across full-page
  // navigations away from onboarding — the Oura OAuth redirect (the URL
  // carries ?code=... on return) and the /get-the-app detour (the URL
  // carries ?resume=<screen> on return). On any other (fresh) entry we
  // ignore it and drop it below — otherwise, on a shared browser, a previous
  // user's abandoned draft could restore their DOB into a new user's session.
  const [isOAuthReturn] = useState(
    () => new URLSearchParams(window.location.search).has('code'),
  );
  // Native deep-link return: DeepLinkHandler routes haiven://oura?status=…
  // back here as /onboarding?oura=<status>. The token exchange already
  // happened server-side before the deep link fired, so there is no ?code
  // — but the entry must gate draft rehydration exactly like the ?code
  // return in case the webview was torn down during OAuth.
  const [isDeepLinkReturn] = useState(
    () => new URLSearchParams(window.location.search).has('oura'),
  );
  // /get-the-app return: ?resume=<screen> jumps straight back to where the
  // user left off (validated 1–7; anything else is treated as a fresh entry).
  const [resumeScreen] = useState(() => {
    const raw = new URLSearchParams(window.location.search).get('resume');
    const n = parseInt(raw, 10);
    return Number.isInteger(n) && n >= 1 && n <= 7 ? n : null;
  });
  const [draft] = useState(() =>
    isOAuthReturn || isDeepLinkReturn || resumeScreen ? loadOnboardingDraft() : {},
  );

  // Fresh start: clear any stale draft a prior, abandoned onboarding left
  // behind (also belt-and-suspenders with the logout-time clear).
  useEffect(() => {
    if (!isOAuthReturn && !isDeepLinkReturn && !resumeScreen) clearOnboardingDraft();
  }, [isOAuthReturn, isDeepLinkReturn, resumeScreen]);

  // Strip ?resume from the URL once consumed so a refresh doesn't re-resume
  // (mirrors the ?code strip in the OAuth-return effect below).
  useEffect(() => {
    if (resumeScreen) window.history.replaceState({}, '', '/onboarding');
  }, [resumeScreen]);

  const [screen, setScreen] = useState(() => resumeScreen || 1);
  // AI data-sharing consent (Screen 2). Asked BEFORE the brain-dump is
  // written so the user knows where their words go before writing them
  // (5.1.1(i)/5.1.2(i)). The choice is PATCHed immediately — by the
  // time handleComplete submits the check-in, the backend gate already
  // reflects it. On PATCH failure we proceed anyway: the backend fails
  // closed (NULL = no AI calls), so the failure mode is privacy-safe.
  const [aiConsentSaving, setAiConsentSaving] = useState(false);
  const [aiDeclineOpen, setAiDeclineOpen] = useState(false);
  const [journalText, setJournalText] = useState(() => draft.journalText || '');
  const [lifeStage, setLifeStage] = useState(() => draft.lifeStage ?? null);
  const [dobMonth, setDobMonth] = useState(() => draft.dobMonth || '');  // '01'..'12' (or '' = unanswered)
  const [dobDay, setDobDay] = useState(() => draft.dobDay || '');        // '01'..'31'
  const [dobYear, setDobYear] = useState(() => draft.dobYear || '');     // 'YYYY'
  const [sex, setSex] = useState(() => draft.sex ?? null);               // 'male' | 'female' | 'prefer_not_to_say' | null
  const [ouraConnected, setOuraConnected] = useState(false);
  const [healthKitConnecting, setHealthKitConnecting] = useState(false);
  const [healthKitError, setHealthKitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [ageBlocked, setAgeBlocked] = useState(false);

  // If user has already completed onboarding, redirect away
  useEffect(() => {
    if (user?.onboarding_completed_at) {
      navigate('/coherence', { replace: true });
    }
  }, [user, navigate]);

  // DOB selector options. Years span the last 100 calendar years
  // (descending, most recent first). Days adapt to the chosen month +
  // year so impossible dates (Feb 30, Apr 31, Feb 29 in a non-leap year)
  // can't be selected. Backend also rejects future DOB via schema.
  const YEAR_OPTIONS = yearOptions();
  const DAY_OPTIONS = dayOptions(dobMonth, dobYear);
  // Complete + valid ISO DOB, or null. Drives the (required) Continue
  // button's enabled state on the demographics screen.
  const dobIso = assembleDob(dobMonth, dobDay, dobYear);

  // Oura entry point gate (utils/features.js), read per render.
  const ouraUI = isOuraUIEnabled();

  const goNext = () => setScreen((s) => s + 1);
  const goBack = () => setScreen((s) => Math.max(1, s - 1));

  const chooseAIConsent = useCallback(async (aiConsent) => {
    if (aiConsentSaving) return;
    setAiConsentSaving(true);
    try {
      await usersApi.setAIConsent(aiConsent, token);
      patchUser({ ai_data_consent: aiConsent });
    } catch {
      // Proceed regardless — NULL fails closed server-side, and the
      // post-onboarding AIConsentModal re-asks if the write was lost.
    } finally {
      setAiConsentSaving(false);
      setAiDeclineOpen(false);
      setScreen(3);
    }
  }, [aiConsentSaving, token, patchUser]);

  // Screen 4: Oura OAuth handler
  const handleConnectOura = useCallback(async () => {
    try {
      // Same platform tag the settings flow (Wearables.jsx) sends: in the
      // native shell the backend's post-exchange redirect then deep-links
      // back via haiven://oura instead of stranding the user in Safari.
      const data = await ouraApi.authorize(token, isNativeIOS() ? 'ios' : undefined);
      if (data?.authorize_url) {
        // Connecting Oura navigates the whole tab to the OAuth provider,
        // which destroys this component's state. Snapshot every answer
        // collected so far (all live on screens 1–4, before this one) so
        // the return mount can rehydrate them — otherwise the required
        // DOB is wiped and the submit-time age gate bounces the user back
        // here in a loop. See utils/onboardingDraft.js.
        saveOnboardingDraft({ journalText, lifeStage, dobMonth, dobDay, dobYear, sex });
        // Open Oura OAuth in same window — callback returns to /onboarding
        window.location.href = data.authorize_url;
      }
    } catch {
      // If Oura auth fails, just skip
      setOuraConnected(false);
      goNext();
    }
  }, [token, journalText, lifeStage, dobMonth, dobDay, dobYear, sex]);

  // Check for Oura callback on mount (returning from OAuth).
  //
  // Previously we set `ouraConnected = true` unconditionally when the
  // `/oura/callback` XHR resolved. That was optimistic: the backend's
  // callback returns a 302 on *both* success and error paths (to
  // `/profile?oura=connected` vs `/profile?oura=error`), so the XHR
  // wrapper couldn't tell them apart and we ended up with
  // `users.oura_connected = true` while `oura_tokens` was still empty
  // whenever the token exchange failed mid-flight.
  //
  // Source of truth is now `GET /oura/status`: it only returns
  // `connected: true` when a real token row exists for the user. We
  // call it after the callback resolves and mirror its answer.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) return;
    const state = params.get('state') || '';

    const finish = (connected) => {
      setOuraConnected(!!connected);
      window.history.replaceState({}, '', '/onboarding');
      setScreen(7);
    };

    ouraApi.callback(code, state, token)
      .then(() => ouraApi.status(token))
      .then((status) => finish(status?.connected === true))
      .catch(() => finish(false));
  }, [token]);

  // Native deep-link return (haiven://oura?status=… → /onboarding?oura=…).
  //
  // Unlike the ?code path there is no exchange left to run — the backend
  // completed it before emitting the deep link — so we only verify via
  // /oura/status (same source-of-truth rule as above) and advance past
  // the wearable step. Keyed on the router's search string, not mount:
  // in the native shell the webview typically never unmounts during
  // OAuth (Safari handles it while the app is backgrounded), so
  // DeepLinkHandler navigates the still-live component here and a
  // mount-only effect would never fire. A status of anything but
  // "connected" still advances (ouraConnected=false) — never strand the
  // user on the connect screen.
  useEffect(() => {
    const ouraReturn = new URLSearchParams(routerLocation.search).get('oura');
    if (!ouraReturn) return;

    const finish = (connected) => {
      setOuraConnected(!!connected);
      window.history.replaceState({}, '', '/onboarding');
      setScreen(7);
    };

    if (ouraReturn !== 'connected') {
      finish(false);
      return;
    }
    ouraApi.status(token)
      .then((status) => finish(status?.connected === true))
      .catch(() => finish(false));
  }, [routerLocation.search, token]);

  // Screen 6: submit everything.
  //
  // Each step is wrapped with ``runStep`` so a failure tags the error with
  // the exact API call that broke. That tag plus status + user context get
  // forwarded to Sentry — without it, a silent catch hides root cause and
  // the only signal the user sees is a generic retry screen.
  const handleComplete = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(false);

    // Assemble DOB (M/D/Y → ISO 'YYYY-MM-DD'); null if incomplete/invalid.
    const dobIso = assembleDob(dobMonth, dobDay, dobYear);

    // Age gate. DOB is required and the Continue button already enforces a
    // complete, valid date upstream; we re-check here and block completion
    // for anyone under MIN_AGE. Non-punitive: surface a kind message and
    // let them go back to fix a mistyped year. The server enforces the
    // same floor in the DOB validator, so a direct API call can't bypass.
    if (!dobIso || ageFromDob(dobIso) < MIN_AGE) {
      setAgeBlocked(true);
      setSubmitting(false);
      return;
    }

    const runStep = async (step, fn) => {
      try {
        return await fn();
      } catch (err) {
        // Attach step label so the top-level catch can tag Sentry events.
        if (err && typeof err === 'object') err.step = step;
        throw err;
      }
    };

    try {
      // Write order rationale:
      // 1. Save the brain-dump FIRST via the async ``/checkin`` upsert.
      //    That endpoint commits the row synchronously and schedules
      //    Claude extraction as a FastAPI BackgroundTask — so the
      //    onboarding path no longer blocks on (or fails because of)
      //    Anthropic availability. If extraction ever fails, the raw
      //    text is still durable in ``daily_checkins`` and can be
      //    re-extracted. This replaces the old blocking
      //    ``/journal/checkin`` call that did a sync Claude message
      //    send + structured-tool-extraction on the critical path.
      // 2. Update the profile (life stage + oura flag + first-login).
      // 3. Stamp ``onboarding_completed_at`` LAST so the "user is
      //    onboarded" flag is only set when every preceding step has
      //    actually succeeded. Previously this stamp happened in the
      //    middle of the sequence, which left users half-onboarded
      //    when the trailing journal write threw (as happened when
      //    the Anthropic account ran out of credits).
      await runStep('dailyCheckin', () => {
        // Local-calendar-day stamp so the row gets bucketed under the
        // user's "today" rather than the server's UTC ``date.today()``.
        // Mirrors the contract in CheckIn.jsx — every read AND every
        // write threads the user's local YYYY-MM-DD end-to-end.
        const d = new Date();
        const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return dailyCheckinApi.upsert(
          { brain_dump: journalText, date: localDate },
          token,
        );
      });

      await runStep('updateProfile', () =>
        authApi.updateProfile(
          {
            profile_json: {
              life_stage_selection: lifeStage,
              oura_connected: ouraConnected,
            },
            first_login_seen: true,
            // Demographics. Empty/unanswered → null so the backend's
            // ``is not None`` guard leaves the column untouched. 'sex'
            // is one of the three allowed tokens or null.
            date_of_birth: dobIso,
            sex: sex || null,
          },
          token,
        ),
      );

      await runStep('completeOnboarding', () =>
        authApi.completeOnboarding(
          { life_stage_selection: lifeStage, oura_connected: ouraConnected },
          token,
        ),
      );

      // Onboarding is committed — drop the OAuth-survival draft so a future
      // re-onboard (or a returning tab) starts clean.
      clearOnboardingDraft();

      // Update local user state
      patchUser({
        first_login_seen: true,
        onboarding_completed_at: new Date().toISOString(),
        // Mirror demographics locally so the post-onboarding backfill
        // modal doesn't fire for a user who just answered here.
        date_of_birth: dobIso,
        sex: sex || null,
      });

      // Navigate to the daily check-in screen.
      navigate('/checkin', { replace: true });
    } catch (err) {
      // Send to Sentry with full flow context. Without this the user-
      // visible retry screen was the only signal, and server-side there
      // was no trail if the error was a 4xx (validation, auth) because
      // HTTPException doesn't route through the global handler.
      Sentry.captureException(err, {
        tags: {
          flow: 'onboarding_submit',
          step: err?.step || 'unknown',
          http_status: err?.status ? String(err.status) : 'none',
        },
        extra: {
          user_id: user?.id,
          life_stage: lifeStage,
          oura_connected: ouraConnected,
          journal_text_length: journalText?.length ?? 0,
          message: err?.message,
        },
      });
      // eslint-disable-next-line no-console
      console.error('[onboarding] submit failed', err);
      setSubmitError(true);
      setSubmitting(false);
    }
  }, [submitting, lifeStage, dobMonth, dobDay, dobYear, sex, ouraConnected, journalText, user, token, patchUser, navigate]);

  // Auto-trigger submit when entering the final (invisible) screen
  useEffect(() => {
    if (screen === 8) {
      handleComplete();
    }
  }, [screen]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <style>{STYLES}</style>
      <div className="ob-root">
        {/* Ambient wash */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 420,
            height: 380,
            pointerEvents: 'none',
            zIndex: 0,
            background:
              'radial-gradient(ellipse at 45% 0%, rgba(158,132,86,0.1) 0%, transparent 65%)',
          }}
        />

        <div style={{ position: 'relative', zIndex: 1, padding: 'max(54px, calc(env(safe-area-inset-top) + 12px)) 24px 52px' }}>
          {/* Header with dots + back (Screens 1–7) */}
          {screen >= 1 && screen <= 7 && (
            <div style={{ marginBottom: 36 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 18,
                }}
              >
                <Wordmark style={{ marginBottom: 0 }} />
                {screen > 1 && <BackButton onClick={goBack} />}
              </div>
              <ProgressDots current={screen - 1} total={7} />
            </div>
          )}

          {/* ============================================================= */}
          {/* SCREEN 1 — Welcome                                            */}
          {/* ============================================================= */}
          {screen === 1 && (
            <div>
              {codeApplied && (
                <div className="fu d1" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  border: `1px solid ${P.border}`, borderRadius: 999,
                  padding: '7px 14px', marginBottom: 18,
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: P.primary, flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 12, color: P.textMid, fontWeight: 300 }}>
                    Your event code is in — full access active.
                  </span>
                </div>
              )}
              <div className="fu d1" style={{ marginBottom: 20 }}>
                <h1
                  className="serif"
                  style={{
                    fontSize: 36,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    color: P.text,
                    lineHeight: 1.15,
                    letterSpacing: '-0.01em',
                  }}
                >
                  Welcome to Haiven.
                </h1>
              </div>

              <div className="fu d2" style={{ marginBottom: 14 }}>
                <p
                  style={{
                    fontSize: 15,
                    color: P.textMid,
                    lineHeight: 1.72,
                    fontWeight: 300,
                    maxWidth: 320,
                  }}
                >
                  Something in you already knows when things are off. This helps
                  you see the pattern.
                </p>
              </div>

              <div className="fu d3" style={{ marginBottom: 44 }}>
                <p
                  style={{
                    fontSize: 13,
                    color: P.textMute,
                    lineHeight: 1.65,
                    fontWeight: 300,
                  }}
                >
                  It starts with a few questions. Takes about 2 minutes.
                </p>
              </div>

              <div className="fu d4">
                <button className="ob-btn" onClick={goNext}>
                  Let's start
                </button>
              </div>

              <div className="fu d5" style={{ textAlign: 'center', marginTop: 18 }}>
                <p style={{ fontSize: 11.5, color: P.textFaint, fontWeight: 300, lineHeight: 1.6 }}>
                  By continuing, you agree to our{' '}
                  <Link to="/privacy" style={{ color: P.primaryMid, textDecoration: 'none' }}>
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </div>
          )}

          {/* ============================================================= */}
          {/* SCREEN 2 — AI data-sharing consent                            */}
          {/* ============================================================= */}
          {screen === 2 && (
            <div key="s2" className="fu">
              <div style={{ marginBottom: 18 }}>
                <h2
                  className="serif"
                  style={{
                    fontSize: 28,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    color: P.text,
                    lineHeight: 1.2,
                    marginBottom: 12,
                  }}
                >
                  Haiven&rsquo;s insights are AI&#8209;powered.
                </h2>
              </div>

              <div style={{ marginBottom: 26 }}>
                <AIDisclosureBody compact />
              </div>

              <button
                className="ob-btn"
                disabled={aiConsentSaving}
                onClick={() => chooseAIConsent(true)}
                style={{ marginBottom: 10 }}
              >
                Turn on AI analysis
              </button>
              <button
                className="ob-btn-ghost"
                disabled={aiConsentSaving}
                onClick={() => setAiDeclineOpen(true)}
              >
                Continue without AI
              </button>

              {aiDeclineOpen && (
                <AIDeclineExplainer
                  submitting={aiConsentSaving}
                  onConfirm={() => chooseAIConsent(false)}
                  onBack={() => setAiDeclineOpen(false)}
                />
              )}
            </div>
          )}

          {/* ============================================================= */}
          {/* SCREEN 3 — What's on your mind                                */}
          {/* ============================================================= */}
          {screen === 3 && (
            <div key="s2" className="fu">
              <div style={{ marginBottom: 24 }}>
                <h2
                  className="serif"
                  style={{
                    fontSize: 30,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    color: P.text,
                    lineHeight: 1.2,
                    marginBottom: 12,
                  }}
                >
                  What's been on your mind lately?
                </h2>
                <p
                  style={{
                    fontSize: 13.5,
                    color: P.textMute,
                    lineHeight: 1.65,
                    fontWeight: 300,
                  }}
                >
                  No wrong answer. Write as little or as much as you want.
                </p>
              </div>

              <div style={{ marginBottom: 28 }}>
                <textarea
                  className="ob-textarea"
                  placeholder="Whatever comes to mind..."
                  value={journalText}
                  onChange={(e) => setJournalText(e.target.value)}
                  autoFocus
                />
                <div
                  style={{
                    fontSize: 11,
                    color:
                      journalText.length >= 20 ? P.textFaint : P.textMute,
                    textAlign: 'right',
                    marginTop: 6,
                    fontWeight: 300,
                  }}
                >
                  {journalText.length} / 20 min
                </div>
              </div>

              <button
                className="ob-btn"
                disabled={journalText.trim().length < 20}
                onClick={goNext}
              >
                Continue
              </button>
            </div>
          )}

          {/* ============================================================= */}
          {/* SCREEN 4 — Life stage signal                                  */}
          {/* ============================================================= */}
          {screen === 4 && (
            <div key="s3" className="fu">
              <div style={{ marginBottom: 24 }}>
                <h2
                  className="serif"
                  style={{
                    fontSize: 28,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    color: P.text,
                    lineHeight: 1.2,
                    marginBottom: 12,
                  }}
                >
                  Which of these feels most like where you are right now?
                </h2>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  marginBottom: 32,
                }}
              >
                {LIFE_STAGES.map((stage) => (
                  <div
                    key={stage.id}
                    className={`ob-stage-card${lifeStage === stage.id ? ' selected' : ''}`}
                    onClick={() => setLifeStage(stage.id)}
                  >
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        flexShrink: 0,
                        border: `2px solid ${lifeStage === stage.id ? P.primary : P.border}`,
                        background:
                          lifeStage === stage.id ? P.primary : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                      }}
                    >
                      {lifeStage === stage.id && (
                        <div
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: P.surface,
                          }}
                        />
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 300,
                        color: P.text,
                        lineHeight: 1.5,
                      }}
                    >
                      {stage.label}
                    </span>
                  </div>
                ))}
              </div>

              <button
                className="ob-btn"
                disabled={!lifeStage}
                onClick={goNext}
              >
                Continue
              </button>
            </div>
          )}

          {/* ============================================================= */}
          {/* SCREEN 5 — Demographics (date of birth + biological sex)      */}
          {/* ============================================================= */}
          {screen === 5 && (
            <div key="s4demo" className="fu">
              <div style={{ marginBottom: 24 }}>
                <h2
                  className="serif"
                  style={{
                    fontSize: 28,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    color: P.text,
                    lineHeight: 1.2,
                    marginBottom: 12,
                  }}
                >
                  A couple of basics.
                </h2>
                <p
                  style={{
                    fontSize: 13.5,
                    color: P.textMute,
                    lineHeight: 1.65,
                    fontWeight: 300,
                  }}
                >
                  A little context helps Haiven read your signal more clearly.
                  Your date of birth is required; sharing your sex is optional.
                </p>
              </div>

              {/* Date of birth — US month / day / year order */}
              <div style={{ marginBottom: 26 }}>
                <label
                  htmlFor="ob-dob-month"
                  style={{
                    display: 'block',
                    fontSize: 13,
                    color: P.textMid,
                    fontWeight: 400,
                    marginBottom: 10,
                  }}
                >
                  When were you born?
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1.1fr', gap: 10 }}>
                  <select
                    id="ob-dob-month"
                    className="ob-input"
                    aria-label="Birth month"
                    value={dobMonth}
                    onChange={(e) => {
                      const m = e.target.value;
                      setDobMonth(m);
                      if (dobDay && Number(dobDay) > daysIn(m, dobYear)) setDobDay('');
                    }}
                  >
                    <option value="">Month</option>
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <select
                    className="ob-input"
                    aria-label="Birth day"
                    value={dobDay}
                    onChange={(e) => setDobDay(e.target.value)}
                  >
                    <option value="">Day</option>
                    {DAY_OPTIONS.map((d) => (
                      <option key={d} value={d}>{Number(d)}</option>
                    ))}
                  </select>
                  <select
                    className="ob-input"
                    aria-label="Birth year"
                    value={dobYear}
                    onChange={(e) => {
                      const y = e.target.value;
                      setDobYear(y);
                      if (dobDay && Number(dobDay) > daysIn(dobMonth, y)) setDobDay('');
                    }}
                  >
                    <option value="">Year</option>
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Biological sex */}
              <div style={{ marginBottom: 14 }}>
                <div
                  style={{
                    fontSize: 13,
                    color: P.textMid,
                    fontWeight: 400,
                    marginBottom: 10,
                  }}
                >
                  Sex
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  {SEX_OPTIONS.map((opt) => (
                    <div
                      key={opt.id}
                      className={`ob-stage-card${sex === opt.id ? ' selected' : ''}`}
                      onClick={() => setSex(opt.id)}
                    >
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          flexShrink: 0,
                          border: `2px solid ${sex === opt.id ? P.primary : P.border}`,
                          background: sex === opt.id ? P.primary : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                        }}
                      >
                        {sex === opt.id && (
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: P.surface,
                            }}
                          />
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 300,
                          color: P.text,
                          lineHeight: 1.5,
                        }}
                      >
                        {opt.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <p
                style={{
                  fontSize: 11.5,
                  color: P.textFaint,
                  fontWeight: 300,
                  lineHeight: 1.6,
                  marginBottom: 28,
                }}
              >
                This helps Haiven tailor your signal to you.
              </p>

              {/* DOB is required: Continue stays disabled until the M/D/Y
                  selectors form a complete, valid date (dobIso != null).
                  Sex remains optional — unselected (null) or
                  'prefer_not_to_say' are both fine. The 18+ age gate is
                  enforced at submit (and server-side). */}
              <button className="ob-btn" disabled={!dobIso} onClick={goNext}>
                Continue
              </button>
            </div>
          )}

          {/* ============================================================= */}
          {/* SCREEN 6 — Oura connection (optional)                         */}
          {/* ============================================================= */}
          {screen === 6 && (
            <div key="s4" className="fu">
              {/* Oura entry point gate (utils/features.js): the native iOS
                  build hides every Oura element on this step while
                  VITE_IOS_OURA_ENABLED is off (App Store 1.0) and shows
                  an Apple Health-only version instead. Web is unchanged. */}
              {ouraUI ? (
              <>
              {/* No hero image here: the previous render imitated Oura's
                  product design under Haiven branding — a Guideline 5.2.1
                  (third-party IP) exposure flagged in App Review. */}

              {/* Headline */}
              <h2
                className="serif"
                style={{
                  fontSize: 32,
                  fontStyle: 'italic',
                  fontWeight: 400,
                  color: P.text,
                  lineHeight: 1.15,
                  marginBottom: 24,
                }}
              >
                Connect your Oura.
              </h2>

              {/* Benefit paragraphs */}
              <div style={{ marginBottom: 28 }}>
                <p style={{
                  fontSize: 14, color: P.textMid, lineHeight: 1.75,
                  fontWeight: 300, marginBottom: 16,
                }}>
                  Some things show up in your sleep and energy before you
                  notice them. Connecting Oura brings those signals into
                  Haiven alongside your check-ins.
                </p>
                <p style={{
                  fontSize: 14, color: P.textMid, lineHeight: 1.75,
                  fontWeight: 300, marginBottom: 16,
                }}>
                  When you connect, your Body signal draws on your wearable
                  data alongside how you&rsquo;re feeling.
                </p>
                <p style={{
                  fontSize: 14, color: P.textMid, lineHeight: 1.75,
                  fontWeight: 300,
                }}>
                  Users with wearables get significantly more accurate signal scores.
                </p>
              </div>

              {/* Social proof */}
              <p style={{
                fontSize: 11, color: P.textFaint, letterSpacing: '0.06em',
                textAlign: 'center', marginBottom: 16, fontWeight: 400,
              }}>
                Oura Gen 3 or later &middot; Takes 30 seconds
              </p>

              {/* CTA */}
              <button className="ob-btn" onClick={handleConnectOura}>
                Connect Oura
              </button>
              </>
              ) : (
              <>
                <h2
                  className="serif"
                  style={{
                    fontSize: 32,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    color: P.text,
                    lineHeight: 1.15,
                    marginBottom: 24,
                  }}
                >
                  Connect Apple Health.
                </h2>
                <div style={{ marginBottom: 28 }}>
                  <p style={{
                    fontSize: 14, color: P.textMid, lineHeight: 1.75,
                    fontWeight: 300, marginBottom: 16,
                  }}>
                    Some things show up in your sleep and energy before you
                    notice them. With your permission, Haiven reads sleep and
                    heart rate from Apple Health alongside your check-ins.
                  </p>
                  <p style={{
                    fontSize: 14, color: P.textMid, lineHeight: 1.75,
                    fontWeight: 300,
                  }}>
                    This is optional. You can skip it and use check-ins on
                    their own.
                  </p>
                </div>
              </>
              )}

              {/* Apple Health — iOS native shell only. Optional like
                  Oura, but a FAILED connect stays on this screen with a
                  visible message instead of silently advancing — the old
                  swallow-and-goNext made "the permission sheet never
                  appeared" indistinguishable from success, with zero
                  telemetry. (connectHealthKit reports the failure to
                  Sentry; the skip link below remains the opt-out.) */}
              {isHealthKitSupported() && (
                <>
                  <button
                    className="ob-btn"
                    style={{ marginTop: 10 }}
                    disabled={healthKitConnecting}
                    onClick={async () => {
                      setHealthKitError("");
                      setHealthKitConnecting(true);
                      try {
                        await connectHealthKit(token);
                        setHealthKitConnecting(false);
                        goNext();
                      } catch (err) {
                        setHealthKitConnecting(false);
                        setHealthKitError(
                          err?.message ||
                            "Couldn't open Apple Health permissions. You can retry, or add it later from Data connections."
                        );
                      }
                    }}
                  >
                    {healthKitConnecting ? "Connecting…" : "Connect Apple Health"}
                  </button>
                  {healthKitError && (
                    <p style={{
                      fontSize: 12, color: '#A3502F', lineHeight: 1.55,
                      textAlign: 'center', marginTop: 10, fontWeight: 400,
                    }}>
                      {healthKitError}
                    </p>
                  )}
                </>
              )}

              {/* Apple Watch / Apple Health pointer — iPhone/iPad *browser*
                  only (mutually exclusive with the native button above:
                  isIOSBrowser() is false inside any Capacitor shell, and
                  false on Android/desktop where HealthKit is irrelevant).
                  HealthKit can't be reached from the web, so this routes
                  to the /get-the-app install screen instead. Same draft
                  snapshot as the Oura redirect — navigating away unmounts
                  this component, and the return (?resume=6) must rehydrate
                  the required DOB or the submit-time age gate loops. */}
              {isIOSBrowser() && (
                <button
                  className="ob-btn-ghost"
                  style={{ marginTop: 10 }}
                  onClick={() => {
                    saveOnboardingDraft({ journalText, lifeStage, dobMonth, dobDay, dobYear, sex });
                    navigate('/get-the-app?from=onboarding');
                  }}
                >
                  Apple Watch or Apple Health? Available in the Haiven app &rarr;
                </button>
              )}

              {/* Skip — plain text link */}
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <button
                  onClick={() => {
                    setOuraConnected(false);
                    goNext();
                  }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, color: P.textFaint, fontWeight: 300,
                    fontFamily: "'DM Sans', sans-serif",
                    padding: '8px 0',
                  }}
                >
                  I&rsquo;ll add it later
                </button>
              </div>
            </div>
          )}

          {/* ============================================================= */}
          {/* SCREEN 7 — Expectations                                       */}
          {/* ============================================================= */}
          {screen === 7 && (
            <div key="s5" className="fu">
              <div style={{ marginBottom: 32 }}>
                <h2
                  className="serif"
                  style={{
                    fontSize: 30,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    color: P.text,
                    lineHeight: 1.2,
                    marginBottom: 28,
                  }}
                >
                  Haiven is learning your rhythms.
                </h2>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 18,
                  }}
                >
                  {[
                    'Check in once a day \u2014 takes 2 minutes.',
                    'After seven check-ins, you\u2019ll see how everything comes together.',
                    'The more you show up, the more Haiven surfaces for you.',
                  ].map((line, i) => (
                    <p
                      key={i}
                      className="fu"
                      style={{
                        animationDelay: `${0.15 + i * 0.12}s`,
                        opacity: 0,
                        fontSize: 15,
                        color: P.textMid,
                        lineHeight: 1.72,
                        fontWeight: 300,
                      }}
                    >
                      {line}
                    </p>
                  ))}
                </div>
              </div>

              <div className="fu d4">
                <button className="ob-btn" onClick={goNext}>
                  Got it
                </button>
              </div>
            </div>
          )}

          {/* ============================================================= */}
          {/* SCREEN 8 — Submission (no visible screen)                     */}
          {/* ============================================================= */}
          {screen === 8 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                textAlign: 'center',
              }}
            >
              {ageBlocked ? (
                <div className="fu d1" style={{ maxWidth: 320 }}>
                  <h2
                    className="serif"
                    style={{
                      fontSize: 26,
                      fontStyle: 'italic',
                      fontWeight: 400,
                      color: P.text,
                      lineHeight: 1.2,
                      marginBottom: 14,
                    }}
                  >
                    Thank you for your interest.
                  </h2>
                  <p
                    style={{
                      fontSize: 14,
                      color: P.textMid,
                      lineHeight: 1.7,
                      fontWeight: 300,
                      marginBottom: 24,
                    }}
                  >
                    Haiven is currently built for adults 18 and over, so we
                    can&rsquo;t set you up just yet. If you entered your date of
                    birth by mistake, you can go back and fix it.
                  </p>
                  <button
                    className="ob-btn-ghost"
                    style={{ maxWidth: 240, margin: '0 auto' }}
                    onClick={() => {
                      setAgeBlocked(false);
                      setScreen(5);
                    }}
                  >
                    Go back
                  </button>
                </div>
              ) : submitError ? (
                <div className="fu d1">
                  <p
                    style={{
                      fontSize: 14,
                      color: P.textMid,
                      lineHeight: 1.65,
                      fontWeight: 300,
                      marginBottom: 24,
                    }}
                  >
                    Something went wrong.
                  </p>
                  <button
                    className="ob-btn"
                    style={{ maxWidth: 240 }}
                    onClick={() => {
                      setSubmitError(false);
                      handleComplete();
                    }}
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <div>
                  {/* Simple loading indicator */}
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: P.primaryMid,
                      opacity: 0.6,
                      animation: 'pulse 1.2s ease-in-out infinite',
                    }}
                  />
                  <style>{`@keyframes pulse { 0%,100% { opacity: 0.6; } 50% { opacity: 0.2; } }`}</style>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
